import { useContext, useMemo } from 'react';

import type { AnalyticsProps, AnalyticsPropsType } from '../types';
import { AnalyticsContext } from './AnalyticsProvider';

// Initially the events were difined in an events file.
// We hit a problem while generating the type declaration files
// where the events file and this file would be out of sync and brake CI.
// It was non-deterministic.
// We merge the files and we're declaring types here as a mitigation.
export type EventName =
  // web vitals
  | 'client_dashboard:web_vitals:largest_contentful_paint'
  | 'client_dashboard:web_vitals:first_contentful_paint'
  | 'client_dashboard:web_vitals:time_to_interactive:dashboard_vaults'

  // shared components
  | 'client_dashboard:filters:hotkey_search_input:focused'
  | 'client_dashboard:scroll_to_top_button:clicked'

  // navigation
  | 'navigation:client_dashboard:app_loaded'
  | 'navigation:client_dashboard:page_view'
  | 'navigation:client_dashboard:sidenav_menuheader_clicked'
  | 'navigation:backoffice_dashboard:app_loaded'
  | 'navigation:backoffice_dashboard:page_view'

  // Product Updates
  | 'product_updates:drawer:opened'
  | 'product_updates:drawer:closed'
  | 'product_updates:important_update:displayed'
  | 'product_updates:important_update:closed'
  | 'product_updates:important_update:done'
  | 'product_updates:important_update:next'
  | 'product_updates:important_update:link_clicked'

  // Marketing widgets
  | 'marketing_widgets:widget:viewed'
  | 'marketing_widgets:widget:supressed'
  | 'marketing_widgets:widget:cta_clicked'
  | 'marketing_widgets:widget:carousel_rotated'

  // Create Vault Wizard
  | 'create_vault_wizard:opened'
  | 'create_vault_wizard:next_clicked' // use initiationPage property to see the trigger source
  | 'create_vault_wizard:form_completed' // use status property for failed or successful

  // Operation Detail Page
  | 'operation_detail:vault_policies:accordion_clicked'
  | 'operation_detail:admin_policies:accordion_clicked'

  // Organization Page
  | 'organization_page:policies:drawer_tab_viewed'

  // Activity Page
  | 'activity_page:table:download_button_clicked'
  | 'activity_page:spam_filter:clicked'

  // Statements Page
  | 'statements_page:table:pdf_link_clicked'

  // Reports Page
  | 'reports_page:asset_addresses:csv_button_clicked'
  | 'reports_page:daily_balance:csv_button_clicked'
  | 'reports_page:daily_balance_by_vaults:csv_button_clicked'
  | 'reports_page:daily_balance_by_vault_addresses:csv_button_clicked'

  // Trade Page
  | 'trade_page:trades_table:download_button_clicked'

  // Wallets drawer
  | 'wallets:qr_button_clicked' // clicked button to expand QR code
  | 'wallets:search_input:focused'
  | 'wallets:wallet_card:archive_button_clicked'
  | 'wallets:wallet_card:manage_allowlist_clicked'

  // Archive wallets modal
  | 'archive_wallets_modal:opened'
  | 'archive_wallets_modal:action_button_clicked'

  // Archive wallet confirmation modal
  | 'archive_wallet_confirmation_modal:archive_completed'

  // --- Withdrawals web ---
  | 'withdrawals:drawer:opened' // clicked on Withdraw button in
  | 'withdrawals:drawer:closed'
  | 'withdrawals:drawer:back_clicked' // use initiationPage property to set the page it was clicked
  | 'withdrawals:drawer:continue_clicked' // use initiationPage property to set the page context
  | 'withdrawals:tag_confirmation_modal:skip' // use tagType property to set Memo os Destination tag
  | 'withdrawals:tag_confirmation_modal:add_tag' // use tagType property to set Memo os Destination tag
  // use action property to set if clicked on X or add
  | 'withdrawals:withdrawal:form_completed' // use status property for failed or success
  | 'withdrawals:address_poisoning:withdrawal_created'
  | 'withdrawals:address_poisoning:withdrawal_attempted'
  | 'withdrawals:address_poisoning:review_completed'

  // IAM
  | 'iam:roles:download_structure'

  // Trusted destinations frontoffice
  | 'trusted_dest:add_drawer:opened'
  | 'trusted_dest:add_drawer:closed'
  | 'trusted_dest:add_drawer:back_clicked'
  | 'trusted_dest:add_drawer:continue_clicked'
  | 'trusted_dest:add_drawer:form_completed' // use status property for failed or successful
  | 'trusted_dest:view_drawer:opened'
  | 'trusted_dest:view_drawer:closed'
  | 'trusted_dest:view_drawer:back_clicked'
  | 'trusted_dest:view_drawer:continue_clicked'
  | 'trusted_dest:view_drawer:remove_form_completed'
  | 'trusted_dest:view_drawer:remove_button_clicked'
  | 'trusted_dest:address_poisoning:destination_created'
  | 'trusted_dest:address_poisoning:destination_attempted'
  | 'trusted_dest:address_poisoning:review_completed'

  // ETH Staking
  | 'eth_staking:wizard:opened'
  | 'eth_staking:wizard:closed' // user presses cancel or X button
  | 'eth_staking:back:clicked'
  | 'eth_staking:continue:clicked'
  | 'eth_staking:wizard:flow_completed'

  // Standardized Staking
  | 'standardized_staking:wizard:opened'
  | 'standardized_staking:wizard:closed' // user presses cancel or X button
  | 'standardized_staking:back:clicked'
  | 'standardized_staking:continue:clicked'
  | 'standardized_staking:wizard:flow_completed'
  // used on the stake flow wizard if auto compound is available
  | 'standardized_staking:claim_rewards:toggled'

  // Asset support request
  | 'asset_support_request_modal:opened'

  // Asset select modal
  | 'asset_select_modal:modal:opened'

  // Web3 Portal
  | 'web3_portal:user_guide_opened'
  | 'web3_portal:app_launched'
  | 'web3_portal:view_connection_drawer:opened'
  | 'web3_portal:disconnect_confirmation_modal:opened'
  | 'web3_portal:disconnect_confirmation_modal:app_disconnected'
  | 'web3_portal:open_external_app_confirmation_modal:opened'
  | 'web3_portal:remove_approved_app_confirmation_modal:opened'
  | 'web3_portal:remove_approved_app_confirmation_modal:app_removed'
  | 'web3_portal:configure_approved_app_modal:opened'
  | 'web3_portal:configure_approved_app_modal:changes_saved'
  | 'web3_portal:add_approved_app_modal:opened'
  | 'web3_portal:add_approved_app_modal:approved'
  | 'web3_portal:accept_disclosures_modal:opened'
  | 'web3_portal:accept_disclosures_modal:accepted'

  // Swap page
  | 'swap:sell_token_modal:filter_applied'
  | 'swap:swap_widget:swap_button_clicked'
  | 'swap:token_allowance_modal:opened'
  | 'swap:token_allowance_modal:initiate_button_clicked'
  | 'swap:recent_transactions:operation_drawer_opened'

  // Dashboard
  | 'dashboard_page:tab_clicked'
  | 'dashboard_page:total_balance_tooltip:hovered'
  | 'dashboard_page:vault_card:clicked'

  // Vault page
  | 'vault_page:edit_vault_name:clicked'
  | 'vault_page:wallets_nav:redirected'
  | 'vault_page:vault_id:copied'
  | 'vault_page:tab_clicked'

  // Wallet page
  | 'wallet_page:edit_wallet_name:clicked'
  | 'wallet_page:edit_wallet_name_button:clicked'
  | 'wallet_page:balances_tab:clicked'
  | 'wallet_page:settings_tab:clicked'
  | 'wallet_page:activity_tab:clicked'
  | 'wallet_page:deposit_button:clicked'
  | 'wallet_page:blockchain_explorer:clicked'
  | 'wallet_page:wallet_address:copied'
  | 'wallet_page:wallet_id:copied'
  | 'wallet_page:network_dropdown:opened'
  | 'wallet_page:network_dropdown:network_switched'
  | 'wallet_page:network_dropdown:add_network:clicked'
  | 'wallet_page:add_network_modal:networks_activated'
  | 'wallet_page:wallet_name:edited'
  | 'wallet_page:staking_tab_callout:shown'
  | 'wallet_page:staking_tab_callout:acknowledged'
  | 'wallet_page:staking_tab_callout:dismissed'

  // Wallet Settings table
  | 'wallet_settings_table:vault_id:copied'

  // Vaults page
  // Wallets tab
  | 'vault_wallets_page:search_input:focused'
  | 'vault_wallets_page:name_filter:clicked'
  | 'vault_wallets_page:network_filter:clicked'
  | 'vault_wallets_page:created_at_filter:clicked'
  | 'vault_wallets_page:wallets_download_csv:clicked'
  | 'vault_wallets_page:wallet_address:copied'
  | 'vault_wallets_page:wallet_row:clicked'
  | 'vault_wallets_page:search_input:searched'
  | 'vault_wallets_page:archive_button:clicked'
  | 'vault_wallets_page:download_button:modal_opened'
  | 'vault_wallets_page:show_filters:clicked'
  | 'vault_wallets_page:download_modal:include_archived:clicked'
  | 'vault_wallets_page:vault:clicked'

  // Wallets drawer
  | 'wallets:wallet_card:wallet_details_clicked'

  // Deposit modal
  | 'deposit_modal:address_text:clicked'
  | 'deposit_modal:copy_button:clicked'
  | 'deposit_modal:blockchain_explorer:clicked'
  | 'deposit_modal:manage_allowlist_button:clicked'
  | 'deposit_modal:download_instructions_button:clicked'

  // Wallet allowlist
  | 'wallet_allowlist:asset_toggle:changed'

  // Wallet Creation Wizard
  | 'wallet_creation_wizard:opened'
  | 'wallet_creation_wizard:closed' // user presses cancel or X button
  | 'wallet_creation_wizard:back:clicked'
  | 'wallet_creation_wizard:continue:clicked'
  | 'wallet_creation_wizard:wallet_create:submitted'
  | 'wallet_creation_wizard:flow_completed'
  | 'wallet_creation_wizard:how_wallets_work:clicked'
  | 'wallet_creation_wizard:network:selected'
  | 'wallet_creation_wizard:max_wallets:clicked'
  | 'wallet_creation_wizard:naming_method:changed'

  // Cost basis onboarding tour
  | 'cost_basis_tour:started'
  | 'cost_basis_tour:finished'
  | 'cost_basis_tour:skipped'

  // Deposit Flow Wizard
  | 'deposit_flow_wizard:opened'
  | 'deposit_flow_wizard:asset_selected'
  | 'deposit_flow_wizard:continue:clicked'
  | 'deposit_flow_wizard:back:clicked'
  | 'deposit_flow_wizard:closed'
  | 'deposit_flow_wizard:wallet_create:submitted'
  | 'deposit_flow_wizard:wallet_create:completed'

  // Archive wallets modal
  | 'archive_wallets_modal:wallet_checkbox:clicked'

  // Vault Actions
  | 'vault_page:deposit:clicked'

  // Collateral Management Dashboard
  | 'collateral_management:empty_state:entered'
  | 'collateral_management:empty_state:exited'
  | 'collateral_management:empty_state:clicked:lender'
  | 'collateral_management:empty_state:clicked:borrower'
  | 'collateral_management:empty_state:lead_modal:opened'
  | 'collateral_management:empty_state:lead_modal:closed'
  | 'collateral_management:empty_state:lead_modal:submitted'
  | 'collateral_management:overview:entered'
  | 'collateral_management:overview:exited'
  | 'collateral_management:activity:entered'
  | 'collateral_management:activity:exited'
  | 'collateral_management:alerts:entered'
  | 'collateral_management:alerts:exited'

  // Operations
  | 'operations:spotlight_tour:started'
  | 'operations:spotlight_tour:finished'
  | 'operations:spotlight_tour:skipped'
  | 'operations:quick_filter_card:button_clicked'
  | 'operations:filter_toolbar:add_filter_button'
  | 'operations:filter_toolbar:added_filter'
  | 'operations:filter_toolbar:update_filter_selection'
  | 'operations:filter_toolbar:removed_filter_on_x'
  | 'operations:filter_toolbar:clear_all_filters'
  | 'operations:filter_toolbar:download_button_clicked'
  | 'operations:filter_toolbar:toggle_selected_item'

  // Tri-party Agreements
  | 'tri_party:collateral_breakdown:opened'
  | 'tri_party:chart:timeframe_changed'
  | 'tri_party:table:filtered'
  | 'tri_party:agreement_row:clicked'
  | 'tri_party:activity_row:clicked'
  | 'tri_party:create_agreement:clicked'
  | 'tri_party:collateral_link_out:clicked'
  | 'tri_party:manage_collateral:opened'
  | 'tri_party:manage_collateral:action_selected'

  // Tax center
  | 'tax_center:viewed' // use initialTab property for the tab shown on load
  | 'tax_center:contact_account_team_clicked'

  // Tax center - Documents tab
  | 'tax_documents:viewed'
  | 'tax_documents:filter_applied' // use filterType and value properties
  | 'tax_documents:expected_dates_modal_opened' // use source property for empty_state or menu
  | 'tax_documents:document_viewed'
  | 'tax_documents:download_or_share_clicked' // use action, method, count, documentTypes properties
  | 'tax_documents:download_result' // use success, count, documentTypes properties
  | 'tax_documents:empty_state_viewed' // use reason property for no_documents or filtered_empty
  | 'tax_documents:error_state_viewed'

  // Tax certification - every event uses formType, submissionId, pageId,
  // surface and mode properties
  | 'tax_certification:card_viewed' // use status property for not_started or complete
  | 'tax_certification:modal_opened'
  | 'tax_certification:reused' // use taxFormType property
  | 'tax_certification:token_failed' // use attempt property: 1 for the first load of an open, plus 1 for each retry or 401 re-mint
  | 'tax_certification:retry_clicked' // use attempt property for the attempt the click starts
  | 'tax_certification:session_expired'
  | 'tax_certification:load_failed' // use reason property for chunk or sdk
  | 'tax_certification:step_viewed' // use stepId, stepNumber, totalSteps, percentComplete properties
  | 'tax_certification:status_confirmed' // use taxFormType, dataCollectionStatus properties
  | 'tax_certification:submit_failed'
  | 'tax_certification:completed' // use taxFormType property
  | 'tax_certification:modal_closed'; // use lastStepId, percentComplete, stage properties

export const useAnalytics = () => useContext(AnalyticsContext);

/**
 * Hook to send analytics events with the correct platform and context
 */
export const useAnalyticsProps = ({
  platform,
  context,
  debug = false,
}: AnalyticsProps): AnalyticsPropsType => {
  const { track } = useAnalytics();

  const analyticsProps = useMemo(
    () => ({
      analyticsFn: (eventName: string, payload?: object) => {
        const analyticsEventPayload = {
          name: `${platform}:${context}:${eventName}`,
          ...(payload ? { data: payload } : {}),
        };
        if (debug) {
          console.info(analyticsEventPayload);
        } else {
          track(analyticsEventPayload);
        }
      },
    }),
    [platform, context, debug, track],
  );

  return analyticsProps;
};
